import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import tokens from '../theme/tokens';

// 기업회원 배송조회 상세 — 단계(접수 → 배차 → 픽업지 이동 → 도착지 이동 → 완료), 진행 막대, 남은 거리·시간.
// tracking: 서버 deliveryList 의 tracking { phase, label, remainingKm, remainingMin, progress, target, riderName, carType, pickupTime, source, updatedAt }
const STEPS = [
  { key: 'accepted', label: '접수' },
  { key: 'assigned', label: '배차' },
  { key: 'to_pickup', label: '픽업지로' },
  { key: 'to_dest', label: '도착지로' },
  { key: 'done', label: '완료' },
];
const PHASE_INDEX = { waiting: 1, to_pickup: 2, to_dest: 3, done: 4, cancelled: -1 };

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    padding: '18px 18px 16px',
    borderRadius: 14,
    backgroundColor: tokens.surface,
    boxShadow: tokens.cardShadow,
  },
  head: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  phase: { fontSize: 16, fontWeight: 700, letterSpacing: -0.8, color: tokens.ink },
  rider: { fontSize: 12, color: tokens.muted, letterSpacing: -0.4 },
  figures: {
    display: 'flex',
    gap: 10,
    marginBottom: 14,
  },
  figure: {
    flex: '1 1 0',
    padding: '12px 14px',
    borderRadius: 12,
    backgroundColor: tokens.primarySoft,
    '& small': { display: 'block', fontSize: 12, color: tokens.muted, letterSpacing: -0.4 },
    '& b': { display: 'block', marginTop: 4, fontSize: 22, fontWeight: 700, letterSpacing: -1, color: tokens.primaryDark },
    '& b span': { fontSize: 13, marginLeft: 2, color: tokens.text, fontWeight: 500 },
  },
  bar: {
    position: 'relative',
    height: 6,
    margin: '6px 0 14px',
    borderRadius: 3,
    backgroundColor: tokens.lineSoft,
    overflow: 'hidden',
  },
  fill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 3,
    backgroundImage: 'linear-gradient(to right, #ff925c, #f77c40)',
    transition: 'width .4s ease',
  },
  steps: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  step: {
    flex: '1 1 0',
    textAlign: 'center',
    fontSize: 12,
    letterSpacing: -0.4,
    color: tokens.placeholder,
    '&:before': {
      content: '""',
      display: 'block',
      width: 10,
      height: 10,
      margin: '0 auto 6px',
      borderRadius: 5,
      border: `2px solid ${tokens.line}`,
      backgroundColor: tokens.surface,
    },
  },
  stepDone: {
    color: tokens.text,
    '&:before': { borderColor: tokens.primary, backgroundColor: tokens.primary },
  },
  stepNow: {
    color: tokens.primaryDark,
    fontWeight: 700,
    '&:before': { borderColor: tokens.primaryDark, backgroundColor: tokens.surface },
  },
  foot: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 12,
    fontSize: 12,
    color: tokens.muted,
    letterSpacing: -0.4,
  },
  cancelled: { color: tokens.muted },
}));

const fmtMin = (min) => {
  if (min == null) return null;
  if (min < 60) return { value: min, unit: '분' };
  return { value: `${Math.floor(min / 60)}시간 ${min % 60}`, unit: '분' };
};
const fmtKm = (km) => (km == null ? null : km < 1 ? { value: Math.round(km * 1000), unit: 'm' } : { value: km.toFixed(1), unit: 'km' });
const fmtTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

function TrackingDetail({ tracking, className }) {
  const cs = useStyles();
  if (!tracking) return null;
  const idx = PHASE_INDEX[tracking.phase];
  const target = tracking.target === 'pickup' ? '픽업지까지' : tracking.target === 'dest' ? '도착지까지' : '';
  const min = fmtMin(tracking.remainingMin);
  const km = fmtKm(tracking.remainingKm);
  const progress = Math.round((tracking.progress || 0) * 100);
  const cancelled = tracking.phase === 'cancelled';

  return (
    <div className={clsx(cs.root, className)}>
      <div className={cs.head}>
        <span className={clsx(cs.phase, cancelled && cs.cancelled)}>{tracking.label}</span>
        <span className={cs.rider}>{[tracking.riderName && `기사 ${tracking.riderName}`, tracking.carType].filter(Boolean).join(' · ')}</span>
      </div>
      {target && (
        <div className={cs.figures}>
          <div className={cs.figure}>
            <small>{target} 남은 시간</small>
            <b>{min ? <>{min.value}<span>{min.unit}</span></> : '위치 확인 중'}</b>
          </div>
          <div className={cs.figure}>
            <small>{target} 남은 거리</small>
            <b>{km ? <>{km.value}<span>{km.unit}</span></> : '위치 확인 중'}</b>
          </div>
        </div>
      )}
      {!cancelled && (
        <React.Fragment>
          <div className={cs.bar}><div className={cs.fill} style={{ width: `${progress}%` }} /></div>
          <div className={cs.steps}>
            {STEPS.map((s, i) => (
              <div key={s.key} className={clsx(cs.step, i < idx && cs.stepDone, i === idx && cs.stepNow, idx === 4 && i === 4 && cs.stepDone)}>{s.label}</div>
            ))}
          </div>
        </React.Fragment>
      )}
      <div className={cs.foot}>
        <span>{tracking.pickupTime ? `픽업 ${fmtTime(tracking.pickupTime)}` : tracking.allocationTime ? `배차 ${fmtTime(tracking.allocationTime)}` : ''}</span>
        <span>{tracking.updatedAt ? `${fmtTime(tracking.updatedAt)} 기준` : ''}{tracking.source === 'estimate' ? ' · 추정' : ''}</span>
      </div>
    </div>
  );
}

export default TrackingDetail;
